import { observable, action, toJS, computed, makeObservable, runInAction } from 'mobx'
import React from 'react'

import api from '../service/api'
import { toast } from 'react-toastify'

class MeetingStore {
    networkStats = { uplink: 0, downlink: 0, lastUpdated: null };
    sessionStats = { rtt: 0, outgoingBitrate: 0, incomingBitrate: 0 };
    lastException = null;

    currentlyPresentedPageRedoStack
    currentlyPresentedPageUndoStack
    currentMeetingAccessDetails
    meetingRoomLayoutSettings
    remoteUsersPresentStatus
    presentedDocElements
    currentMeetingSettings
    readOnlyZoomAndPan
    meetingChatsDetails
    meetingUsersDetails
    presentingDocument
    notificationTimers
    usersPermissions
    pendingRequests
    handRaiseQueue
    currentMeeting
    sharingUsers
    meetingState
    remoteUsers
    videoTrack
    audioTrack
    localUser
    chats


    lastNotificationTime = 0;
    notificationCooldown = 1000 * 60;

    constructor () {
        this.currentMeetingAccessDetails = {
            unauth: true,
            hasAccess: false,
            sessionEnded: true,
            needsAliveLogin: true
        }
        this.meetingRoomLayoutSettings = {
            leftSideCollapseTab: 'participants',
            isPrentationsManagerOpen: false,
            isLeftSideCollapseOpen: false,
            isPollDialogMinimized: true,
            hideHandsRaisedCard: false,
            isInFullscreen: false,
        }
        this.meetingChatsDetails = {
            currentChatId: '',
            recentChats: [],
            indicators: {
                tabs: {},
                chatbutton: false,
                users: {}
            }
        }
        this.eventsStackTail = {}
        this.meetingState = {
            presentingVirtualBoard: false,
            presentingFileId: null,
            pipWindowEnabled: false,
            duplicateSession: false,
            isSomeoneSharing: false,
            isStartingVideo: false,
            isAudioPublished: false,
            isVideoPublished: false,
            isCameraDisabled: false,
            activePresenter: null,
            isMicDisabled: false,
            currentPollIndex: 0,
            waitingToJoin: false,
            isRecording: false,
            isCameraOn: false,
            isAudioOn: false,
            isSharing: false,
            sharingState: {
                screen: false,
                audio: false,
                audioEnabled: false
            },
            videoEffectValues: {
                img: 0, blur: 1, color: 0
            },
            layout: {
                layoutMode: "focused"
            },
            videoEffect: "blur",
            isJoined: false,
            speakerId: undefined,
            cameraId: undefined,
            micId: undefined,
            raisedHand: false,
        }

        this.currentlyPresentedPageUndoStack = []
        this.currentlyPresentedPageRedoStack = []
        this.readOnlyZoomAndPan = { zoom: 1, pan: { x: 0, y: 0 } }
        this.presentedDocElements = {}
        this.remoteUsersPresentStatus = {}
        this.currentMeetingSettings = {}
        this.presentingDocument = null
        this.notificationTimers = {}
        this.meetingUsersDetails = {}
        this.currentMeeting = null
        this.pendingRequests = []
        this.handRaiseQueue = []
        this.usersPermissions = {}
        this.videoProcessor = null
        this.sharingUsers = []
        this.videoTrack = null
        this.audioTrack = null
        this.remoteUsers = []
        this.localUser = {}
        this.ioDevices = {
            speakers: [],
            mics: [],
            cameras: []
        }
        this.pollResults = []
        this.pollQueue = []
        this.newJoins = []
        this.chats = {}
        this.polls = []

        makeObservable( this, {
            meetingRoomLayoutSettings: observable,
            currentMeetingSettings: observable,
            readOnlyZoomAndPan: observable,
            presentedDocElements: observable,
            meetingChatsDetails: observable,
            presentingDocument: observable,
            meetingUsersDetails: observable,
            usersPermissions: observable,
            pendingRequests: observable,
            currentMeeting: observable,
            handRaiseQueue: observable,
            videoProcessor: observable,
            sessionStats: observable,
            networkStats: observable,
            meetingState: observable,
            remoteUsers: observable,
            pollResults: observable,
            videoTrack: observable,
            audioTrack: observable,
            localUser: observable,
            pollQueue: observable,
            ioDevices: observable,
            newJoins: observable,
            chats: observable,
            polls: observable,


            setCurrentMeetingAccessDetails: action,
            setMeetingRoomLayoutSettings: action,
            setPresentedDocElements: action,
            addOrRemovePendingRequests: action,
            setPresentingDocumentData: action,
            fetchMeetingChatsAndPoll: action,
            updateMeetingUsersDetails: action,
            addOrRemoveSharingUsers: action,
            setMeetingChatsDetails: action,
            addOrUpdateRemoteUsers: action,
            updateUsersPermissions: action,
            setPresentingDocument: action,
            setReadOnlyZoomAndPan: action,
            setRequestPendingList: action,
            playNotificationSound: action,
            resetVirtualboardData: action,
            updateMeetingDetails: action,
            setUsersPermissions: action,
            setLocalUserDetails: action,
            setNetworkException: action,
            setLocalVideoTrack: action,
            resetOldJoinedData: action,
            setLocalAudioTrack: action,
            setMeetingSettings: action,
            setMeetingDetails: action,
            setVideoProcessor: action,
            retrievePollQueue: action,
            toggleFullScreen: action,
            removeRemoteUser: action,
            updatePollQueue: action,
            setMeetingState: action,
            addOrRemovePoll: action,
            setNetworkStats: action,
            setSessionStats: action,
            setPollResults: action,
            setRaisedHands: action,
            setRoomChats: action,
            fetchMeeting: action,
            setIoDevices: action,
            setNewJoins: action,
            updatePoll: action,

            getCurrentMeetingAccessDetails: computed,
            getMeetingRoomLayoutSettings: computed,
            getPresentedDocPageElements: computed,
            getCurrentMeetingSettings: computed,
            getMeetingChatsDetails: computed,
            getMeetingUsersDetails: computed,
            getPresentingDocument: computed,
            getReadOnlyZoomAndPan: computed,
            getUsersPermissions: computed,
            getPendingRequests: computed,
            getLocalVideoTrack: computed,
            getLocalAudioTrack: computed,
            getVideoProcessor: computed,
            getCurrentMeeting: computed,
            getRaisedHands: computed,
            getSharingUsers: computed,
            getMeetingState: computed,
            getRemoteUsers: computed,
            getPollResults: computed,
            getLocalUser: computed,
            getIoDevices: computed,
            getPollQueue: computed,
            getNewJoins: computed,
            getChats: computed,
            getPolls: computed,
        } )
    }

    // STATS
    setNetworkStats ( stats ) {
        this.networkStats = stats
    }

    setSessionStats ( stats ) {
        this.sessionStats = stats
    }

    setNetworkException ( ex ) {
        this.lastException = ex
    }

    // PRESENTATION
    setPresentingDocumentData ( data ) {
        runInAction( () => {
            this.presentedDocElements = data.elements
        } )
    }

    setPresentedDocElements ( pageId, elements ) {
        runInAction( () => {
            this.presentedDocElements[pageId] = elements
        } )
    }

    setReadOnlyZoomAndPan ( zoom, pan ) {
        runInAction( () => {
            this.readOnlyZoomAndPan.zoom = zoom
            this.readOnlyZoomAndPan.pan = pan
        } )
    }

    resetVirtualboardData () {
        runInAction( () => {

        } )
    }

    // LAYOUT

    setMeetingRoomLayoutSettings ( changes ) {
        runInAction( () => {
            for ( const key in changes ) {
                if ( Object.prototype.hasOwnProperty.call( changes, key ) ) {
                    this.meetingRoomLayoutSettings[key] = changes[key]
                }
            }
        } )
    }


    toggleFullScreen ( element ) {
        if ( !document.fullscreenElement ) {
            if ( element ) {
                if ( element.requestFullscreen ) {
                    element.requestFullscreen()
                } else if ( element.webkitRequestFullscreen ) {
                    // Safari
                    element.webkitRequestFullscreen()
                } else if ( element.msRequestFullscreen ) {
                    // IE/Edge
                    element.msRequestFullscreen()
                }
                runInAction( () => {
                    this.meetingRoomLayoutSettings.isInFullscreen = true
                } )
            }
        } else {
            document.exitFullscreen()
            runInAction( () => {
                this.meetingRoomLayoutSettings.isInFullscreen = false
            } )
        }
    }

    setIoDevices ( devices ) {
        runInAction( () => {
            for ( const key in devices ) {
                if ( Object.prototype.hasOwnProperty.call( devices, key ) ) {
                    this.ioDevices[key] = devices[key]
                }
            }
        } )
    }

    // CHATS
    setMeetingChatsDetails ( changes ) {
        runInAction( () => {
            for ( const key in changes ) {
                if ( Object.prototype.hasOwnProperty.call( changes, key ) ) {
                    this.meetingChatsDetails[key] = changes[key]
                }
            }
        } )
    }

    setRoomChats ( messageData, chatId ) {
        runInAction( () => {
            if ( this.chats[chatId] ) {
                this.chats[chatId].push( messageData )
            } else {
                const chatExists = this.meetingChatsDetails.recentChats.find( c => c.chat_id === chatId )
                if ( !chatExists && messageData.type === "private" ) {
                    this.meetingChatsDetails.recentChats.push( {
                        name: messageData.name,
                        user_id: messageData.user_id,
                        role: messageData.role,
                        chat_id: chatId
                    } )
                }
                this.chats[chatId] = [messageData]
            }
            if ( chatId !== this.meetingChatsDetails.currentChatId ) {
                this.meetingChatsDetails.indicators.tabs[chatId] = ( this.meetingChatsDetails.indicators.tabs[chatId] || 0 ) + 1
            }
            if ( !this.meetingRoomLayoutSettings.isLeftSideCollapseOpen || chatId !== this.meetingChatsDetails.currentChatId || this.meetingRoomLayoutSettings.leftSideCollapseTab !== 'chats' )
                this.queueNotificationSound( chatId )
            this.meetingChatsDetails.indicators.chatbutton = !this.meetingRoomLayoutSettings.isLeftSideCollapseOpen || chatId !== this.meetingChatsDetails.currentChatId || this.meetingRoomLayoutSettings.leftSideCollapseTab !== 'chats'
            // if ( chatId !== this.meetingChatsDetails.currentChatId || !this.meetingRoomLayoutSettings.isLeftSideCollapseOpen || this.meetingRoomLayoutSettings.leftSideCollapseTab !== 'chats' ) {
            //     this.meetingChatsDetails.indicators.chatbutton = true
            //     if ( messageData.type !== "room" )
            //         this.meetingChatsDetails.indicators.users = { ...this.meetingChatsDetails.indicators.users, [messageData.user_id]: true }
            // }
        } )
    }

    // POLLS 
    addOrRemovePoll ( poll, action = "add" ) {
        runInAction( () => {
            if ( action === "add" ) {
                this.polls.push( poll )
                this.meetingRoomLayoutSettings.isPollDialogMinimized = false
            } else {
                const index = this.polls.findIndex( p => p.id === poll.id )
                if ( this.meetingState.currentPollIndex === index ) {
                    this.meetingState.currentPollIndex = this.meetingState.currentPollIndex > 0 ? this.meetingState.currentPollIndex - 1 : this.meetingState.currentPollIndex
                }
                this.polls.splice( index, 1 )
            }

        } )
    }

    updatePoll ( poll, id ) {
        runInAction( () => {
            if ( this.localUser.meeting_role === 'meeting-host' ) {
                const pollIndex = this.polls.findIndex( p => p.id === id )
                if ( pollIndex >= 0 )
                    this.polls[pollIndex] = poll

                const pollResultsIndex = this.pollResults.findIndex( p => p.id === id )
                if ( pollResultsIndex >= 0 )
                    this.pollResults[pollResultsIndex] = poll
                else
                    this.pollResults.push( poll )
            } else {
                const pollResultsIndex = this.pollResults.findIndex( p => p.id === id )
                if ( pollResultsIndex >= 0 )
                    this.pollResults[pollResultsIndex] = poll
                else
                    this.pollResults.push( poll )
            }
            const meetingId = poll.meeting_id || poll.online_meeting_id
            if ( this.meetingChatsDetails.currentChatId !== `${meetingId}-polls` ) {
                this.meetingChatsDetails.indicators.tabs[`${meetingId}-polls`] = ( this.meetingChatsDetails.indicators.tabs[`${meetingId}-polls`] ?? 0 ) + 1
            }
        } )
    }

    setPollResults ( poll ) {
        runInAction( () => {
            const pollIndex = this.pollResults.findIndex( p => p.id === poll.id )
            if ( pollIndex >= 0 ) {
                this.pollResults[pollIndex] = poll
            } else
                this.pollResults.push( poll )
        } )
    }

    updatePollQueue ( action, data = {} ) {
        try {
            switch ( action ) {
                case "add":
                    if ( !data || !Array.isArray( data ) )
                        return
                    this.pollQueue.push( ...data )
                    break

                case "remove":
                    const { index } = data
                    if ( Number.isInteger( index ) && index >= 0 ) {
                        const [removed] = this.pollQueue.splice( index, 1 )
                        return toJS( removed )
                    }
                    break

                case "reorder": {
                    const { from, to } = data
                    if ( to < 0 || to >= this.pollQueue.length ) return

                    const [movedItem] = this.pollQueue.splice( from, 1 )
                    this.pollQueue.splice( to, 0, movedItem )
                }
                    break
                case "shift":
                    return toJS( this.pollQueue.shift() )
                default:
                    break
            }
        } catch ( err ) {
            console.log( err )

        } finally {
            localStorage.setItem( `online_session_poll_queue`, JSON.stringify( { [this.currentMeeting.id]: this.pollQueue } ) )
        }
    }

    retrievePollQueue () {
        if ( this.pollQueue && this.pollQueue.length === 0 ) {
            let existingPolls = localStorage.getItem( `online_session_poll_queue` )
            if ( existingPolls ) {
                existingPolls = JSON.parse( existingPolls )
                existingPolls = existingPolls[this.currentMeeting.id]
                this.pollQueue = existingPolls || []
            }
        }
    }

    // HAND RAISES
    setRaisedHands ( newQueue = [] ) {
        runInAction( () => {
            this.handRaiseQueue = newQueue
            if ( this.meetingState.raisedHand && !newQueue.find( hr => hr.user_id === this.localUser.user_id ) ) {
                this.meetingState.raisedHand = false
            }
        } )
    }

    // MEETINGS 
    async fetchMeetingChatsAndPoll () {
        try {
            const { data: { data: { chats, polls, pollVotes } } } = await api.onlineMeetings.fetchMeetingChatsAndPoll( this.currentMeeting.id )
            runInAction( () => {
                const chatsMap = {}
                for ( const chat of chats ) {
                    if ( chatsMap[chat.chat_id] ) {
                        if ( chat.type === "room" || ( chat.type === "private" && ( this.localUser.user_id === chat.from_user_data.user_id || this.localUser.user_id === chat.to_user_data.user_id ) ) )
                            chatsMap[chat.chat_id].push( chat )
                    } else {
                        if ( chat.type === "room" || ( chat.type === "private" && ( this.localUser.user_id === chat.from_user_data.user_id || this.localUser.user_id === chat.to_user_data.user_id ) ) ) {
                            chatsMap[chat.chat_id] = [chat]
                            const chatExists = this.meetingChatsDetails.recentChats.find( c => c.chat_id === chat.chat_id )

                            if ( !chatExists && chat.type === "private" && ( this.localUser.user_id === chat.from_user_data.user_id || this.localUser.user_id === chat.to_user_data.user_id ) ) {
                                this.meetingChatsDetails.recentChats.push( {
                                    name: this.localUser.user_id === chat.from_user_data.user_id ? chat.to_user_data.name : chat.from_user_data.name,
                                    user_id: this.localUser.user_id === chat.from_user_data.user_id ? chat.to_user_data.user_id : chat.from_user_data.user_id,
                                    role: chat.from_user_data.role,
                                    chat_id: chat.chat_id
                                } )
                            }
                        }
                    }
                }

                this.chats = chatsMap
                const pollsList = []
                const isHost = this.currentMeeting.hosts.find( host => host.user_id === this.localUser.user_id )
                const pollsAndVotesMap = {}
                for ( const vote of pollVotes ) {
                    const voteDetails = {
                        user_id: vote.voter_id,
                        name: vote.voter_data.name,
                        poll_vote: vote.poll_vote
                    }
                    if ( pollsAndVotesMap[vote.poll_id] ) {
                        pollsAndVotesMap[vote.poll_id].push( voteDetails )
                    } else {
                        pollsAndVotesMap[vote.poll_id] = [voteDetails]
                    }
                }

                for ( const poll of polls ) {
                    poll.votes = Array.from( { length: poll?.options?.length }, () => [] )
                    for ( const pollVoteDetails of pollsAndVotesMap[poll.id] || [] ) {
                        poll.votes[+( pollVoteDetails.poll_vote )].push( pollVoteDetails )
                    }
                    poll.totalVotes = ( pollsAndVotesMap[poll.id] || [] ).length
                    pollsList.push( poll )

                }
                console.log( "POLLS LIST:", pollsList )

                if ( isHost ) {
                    this.polls = pollsList
                    this.pollResults = pollsList
                } else
                    this.pollResults = pollsList
                if ( pollsList.length > 0 ) {
                    this.meetingRoomLayoutSettings.isPollDialogMinimized = false
                }
            } )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Failed to fetch meeting chats and polls" )
        }
    }
    async fetchMeeting ( meetingId, updates = { settings: true, accessDetails: true } ) {
        try {
            const { data: { data: meeting, unauth, sessionEnded, hasAccess, needsAliveLogin } } = await api.onlineMeetings.fetchMeeting( meetingId )
            runInAction( () => {
                this.currentMeeting = meeting
                if ( updates.settings )
                    this.currentMeetingSettings = meeting.meeting_settings
                this.meetingChatsDetails.currentChatId = meeting.id
                if ( updates.accessDetails ) {
                    this.setCurrentMeetingAccessDetails( {
                        unauth,
                        sessionEnded,
                        hasAccess,
                        needsAliveLogin,
                    } )
                }
            } )
            return { success: true, meeting, unauth, sessionEnded, hasAccess, needsAliveLogin }
        } catch ( err ) {
            console.log( err )
            return { success: false, status: err?.response?.status, message: err?.response?.data?.message || `Failed to fetch meeting details!`, err }
        }
    }

    async updateMeetingDetails ( meetingId, updateData ) {
        try {
            const { data: { data: meeting } } = await api.onlineMeetings.updateMeetingDetails( meetingId, updateData )
            this.setMeetingDetails( meeting )
            return { success: true, data: meeting }
        } catch ( err ) {
            console.log( err )
            return { success: false, status: err?.response?.status, message: err?.response?.data?.message || `Failed to update meeting details!` }
        }
    }

    setCurrentMeetingAccessDetails ( accessDetails = {} ) {
        runInAction( () => {
            for ( const key in accessDetails ) {
                if ( Object.prototype.hasOwnProperty.call( accessDetails, key ) ) {
                    this.currentMeetingAccessDetails[key] = accessDetails[key]
                }
            }
        } )
    }

    setMeetingDetails ( data ) {
        runInAction( () => {
            this.currentMeeting = data
            this.currentMeetingSettings = data.meeting_settings
        } )
    }

    resetOldJoinedData () {
        runInAction( () => {
            this.remoteUsers = []
            this.chats = {}
            this.polls = []
            this.pollQueue = []
            this.pollResults = []
            this.meetingState.isSharing = false
            this.meetingState.isSomeoneSharing = false
            this.meetingState.presentingVirtualBoard = false
            this.pendingRequests = []
            this.handRaiseQueue = []
            this.presentedDocElements = {}
            this.presentingDocument = null
        } )
    }

    setMeetingSettings ( values ) {
        runInAction( () => {
            this.currentMeetingSettings = values
        } )
    }

    setPresentingDocument ( document ) {
        runInAction( () => {
            this.presentingDocument = document
        } )
    }

    setLocalVideoTrack ( track ) {
        runInAction( () => {
            this.videoTrack = track
            this.localUser.videoTrack = track
        } )
    }

    setLocalAudioTrack ( track ) {
        runInAction( () => {
            this.audioTrack = track
            this.localUser.audioTrack = track
        } )
    }

    setMeetingState ( changes ) {
        runInAction( () => {
            for ( const key in changes ) {
                if ( Object.prototype.hasOwnProperty.call( changes, key ) ) {
                    this.meetingState[key] = changes[key]
                }
            }
        } )
    }

    // USERS RELATED ACTIONS

    setNewJoins ( newValue ) {
        runInAction( () => {
            this.newJoins = newValue
        } )
    }

    addOrUpdateRemoteUsers ( users = [], source, action = "added", showToast = true ) {

        runInAction( () => {
            if ( action === "added" ) {
                for ( const user of users ) {
                    let userId = source === 'agora-server' ? user.uid : user.user_id

                    if ( userId === this.localUser.user_id || ["recorder0", "10101010"].includes( userId ) || userId?.match( /^[0-9]{6}$/ ) ) continue
                    const userIndex = this.remoteUsers.findIndex( u => u.user_id === userId )

                    if ( userIndex >= 0 ) {

                        const updatedUserData = { ...this.remoteUsers[userIndex] }
                        if ( source === 'agora-server' ) {
                            updatedUserData.videoTrack = user.videoTrack
                            updatedUserData.audioTrack = user.audioTrack
                            updatedUserData.isAudioOn = Boolean( user.audioTrack )
                            updatedUserData.isVideoOn = Boolean( user.videoTrack )
                        } else {
                            updatedUserData.user_id = userId
                            updatedUserData.name = user.name
                            updatedUserData.role = user.role
                            updatedUserData.email = user.email
                            updatedUserData.alive_user = user.alive_user
                            updatedUserData.meeting_role = user.meeting_role
                            updatedUserData.sharing = user.sharing
                            updatedUserData.type = user.type
                            updatedUserData.activePresenter = user.activePresenter
                            updatedUserData.color_index = user.color_index
                        }
                        this.remoteUsers[userIndex] = updatedUserData
                    } else {
                        const userData = {}
                        if ( source === 'agora-server' ) {
                            userData.user_id = userId
                            userData.videoTrack = user.videoTrack
                            userData.audioTrack = user.audioTrack
                        } else {
                            userData.user_id = userId
                            userData.name = user.name
                            userData.role = user.role
                            userData.email = user.email
                            userData.alive_user = user.alive_user
                            userData.meeting_role = user.meeting_role
                            userData.sharing = user.sharing
                            userData.type = user.type
                            userData.color_index = user.color_index
                            userData.activePresenter = user.activePresenter
                            if ( showToast && !user.sharing && user.name && !user.user_id?.includes( 'sharing' ) ) {
                                this.newJoins.push( { name: user.name, color_index: user.color_index } )
                            }
                        }
                        if ( user.sharing )
                            this.remoteUsers.splice( 0, 0, userData )
                        else
                            this.remoteUsers.push( userData )
                        console.log( "New UserData", userData )
                    }
                }
            } else if ( action === "removed" ) {
                for ( const user of users ) {
                    let userId = source === 'agora-server' ? user.uid : user.user_id
                    const userIndex = this.remoteUsers.findIndex( u => u.user_id === userId )
                    if ( userIndex >= 0 )
                        this.remoteUsers.splice( userIndex, 1 )
                }
            } else if ( action === "set" ) {
                const newList = []
                for ( const user of users ) {
                    let userId = source === 'agora-server' ? user.uid : user.user_id
                    if ( userId === this.localUser.user_id || ["recorder0", "10101010"].includes( userId ) || userId?.match( /^[0-9]{6}$/ ) ) continue
                    const userData = {}
                    if ( source === 'agora-server' ) {
                        userData.user_id = userId
                        userData.videoTrack = user.videoTrack
                        userData.audioTrack = user.audioTrack
                    } else {
                        userData.user_id = userId
                        userData.name = user.name
                        userData.role = user.role
                        userData.email = user.email
                        userData.alive_user = user.alive_user
                        userData.meeting_role = user.meeting_role
                        userData.sharing = user.sharing
                        userData.type = user.type
                        userData.color_index = user.color_index
                        userData.activePresenter = user.activePresenter

                    }
                    if ( userId?.includes( "sharing" ) )
                        newList.splice( 0, 0, userData )
                    else
                        newList.push( userData )
                }

                this.remoteUsers = newList
            }
            if ( !this.meetingState.presentingVirtualBoard ) {
                const activePresenter = this.remoteUsers.find( usr => usr.activePresenter )
                if ( activePresenter ) {
                    this.meetingState.activePresenter = { ...activePresenter }
                } else
                    this.meetingState.activePresenter = null
            }
        } )
    }

    updateMeetingUsersDetails ( usersDetails ) {
        runInAction( () => {
            if ( Array.isArray( usersDetails ) ) {
                for ( const user of usersDetails ) {
                    const userId = user.user_id
                    this.meetingUsersDetails[userId] = user
                }
            } else if ( typeof usersDetails === 'object' ) {
                for ( const userId in usersDetails ) {
                    if ( !Object.hasOwn( usersDetails, userId ) ) continue
                    const user = usersDetails[userId]
                    this.meetingUsersDetails[userId] = user
                }
            }
        } )
    }

    addOrRemoveSharingUsers ( user, action ) {
        let userId = user.uid
        if ( userId === this.localUser.user_id || userId === `${this.localUser.user_id}-sharing` ) return
        runInAction( () => {
            if ( action === "add" ) {
                if ( !this.sharingUsers.includes( user.uid ) )
                    this.sharingUsers.push( user.uid )
            } else {
                const userIndex = this.sharingUsers.findIndex( u => u === user.uid )
                if ( userIndex >= 0 )
                    this.sharingUsers.splice( userIndex, 1 )
            }
            if ( this.sharingUsers.length > 0 )
                this.meetingState.isSomeoneSharing = true
            else
                this.meetingState.isSomeoneSharing = false
        } )
    }

    setUsersPermissions ( newValues ) {
        runInAction( () => {
            const cleanedValues = { ...newValues }
            const emptyPermissionObjects = []
            for ( const userId in cleanedValues ) {
                if ( !Object.hasOwn( cleanedValues, userId ) ) continue
                const userPermission = cleanedValues[userId]
                if ( userPermission.mic == null && userPermission.camera == null && userPermission.screenSharing == null )
                    emptyPermissionObjects.push( userId )
            }
            for ( const userId of emptyPermissionObjects ) {
                delete cleanedValues[userId]
            }
            this.usersPermissions = cleanedValues
        } )
    }

    updateUsersPermissions ( userId, newPermissions ) {
        runInAction( () => {
            let cleanedValues = {}
            if ( !userId ) {
                this.usersPermissions = newPermissions
                cleanedValues = { ...newPermissions }
            } else {
                this.usersPermissions[userId] = newPermissions
                cleanedValues = { ...this.usersPermissions, [userId]: { ...newPermissions } }
            }
            const emptyPermissionObjects = []
            for ( const userId in cleanedValues ) {
                if ( !Object.hasOwn( cleanedValues, userId ) ) continue
                const userPermission = cleanedValues[userId]
                if ( userPermission.mic == null && userPermission.camera == null && userPermission.screenSharing == null )
                    emptyPermissionObjects.push( userId )
            }
            for ( const userId of emptyPermissionObjects ) {
                delete cleanedValues[userId]
            }
            this.usersPermissions = cleanedValues
        } )
    }

    removeRemoteUser ( userId, source ) {
        runInAction( () => {
            const index = this.remoteUsers.findIndex( u => u.user_id === userId )
            if ( index >= 0 )
                this.remoteUsers.splice( index, 1 )
        } )
    }

    togglePinUser ( userId ) {
        runInAction( () => {
            const userIndex = this.remoteUsers.findIndex( u => u.user_id === userId )
            if ( userIndex >= 0 ) {
                const flag = !Boolean( this.remoteUsers[userIndex].pinned )
                if ( flag && this.pinnedCount === 5 ) {
                    return toast( "Cannot pin more than 5 users." )
                }
                this.remoteUsers[userIndex].pinned = flag
                this.pinnedCount += flag ? 1 : -1
            }
        } )
    }

    setLocalUserDetails ( details = {} ) {
        runInAction( () => {
            for ( const key in details ) {
                if ( Object.prototype.hasOwnProperty.call( details, key ) ) {
                    this.localUser[key] = details[key]
                }
            }
        } )
    }

    setVideoProcessor ( processor ) {
        runInAction( () => {
            this.videoProcessor = processor
        } )
    }

    // JOIN REQUESTS
    addOrRemovePendingRequests ( usersDetails = [], action = "add" ) {
        for ( const userDetails of usersDetails ) {
            runInAction( () => {
                const requests = this.pendingRequests
                const requestIndex = requests.findIndex( r => r.user_id === userDetails.user_id )
                if ( requestIndex >= 0 ) {
                    if ( action !== "add" )
                        requests.splice( requestIndex, 1 )
                } else {
                    if ( action === "add" )
                        requests.push( userDetails )
                }
            } )
        }
    }

    setRequestPendingList ( newList = [] ) {
        runInAction( () => {
            this.pendingRequests = newList
        } )
    }


    // HELPERS
    queueNotificationSound ( chatId ) {
        // If there's already a timer, reset it
        if ( this.notificationTimers[chatId] ) {
            clearTimeout( this.notificationTimers[chatId] )
        }

        // Start new 30s timer
        this.notificationTimers[chatId] = setTimeout( () => {
            this.playNotificationSound()
            delete this.notificationTimers[chatId] // cleanup
        }, 1000 )
    }

    playNotificationSound () {
        const now = Date.now()

        // Only play if 2 seconds have passed since the last sound
        if ( now - this.lastNotificationTime < this.notificationCooldown ) {
            return
        }

        const sound = document.getElementById( "chatSound" )
        if ( sound ) {
            this.lastNotificationTime = now // Update the timestamp
            console.log( "Playing Notification sound" )

            sound.currentTime = 0
            sound.play().catch( err => console.log( "Autoplay blocked:", err ) )
        }
    }


    // GETTERS


    // Stats Getters
    get networkQualityColor () {
        const quality = this.networkStats.uplink || 1
        if ( quality <= 2 ) return "#4caf50" // Green (Excellent/Good)
        if ( quality === 3 ) return "#ffeb3b" // Yellow (Poor)
        if ( quality === 4 ) return "#ff9800" // Orange (Bad)
        return "#f44336" // Red (Very Bad/Down)
    }

    get networkQualityLabel () {
        const labels = ["Unknown", "Excellent", "Good", "Poor", "Bad", "Very Bad", "Down"]
        return labels[this.networkStats.uplink] || "Unknown"
    }

    get connectionStatus () {
        if ( this.meetingState.connectionLost ) return { label: "Reconnecting", color: "error" }
        if ( !this.meetingState.isJoined ) return { label: "Disconnected", color: "default" }
        return { label: "Connected", color: "success" }
    }

    // Meeting Getters

    get getCurrentMeetingAccessDetails () {
        return toJS( this.currentMeetingAccessDetails )
    }

    get getMeetingRoomLayoutSettings () {
        return toJS( this.meetingRoomLayoutSettings )
    }

    get getMeetingChatsDetails () {
        return toJS( this.meetingChatsDetails )
    }

    get getUsersPermissions () {
        return toJS( this.usersPermissions )
    }

    get getMeetingUsersDetails () {
        return toJS( this.meetingUsersDetails )
    }

    get getPresentedDocPageElements () {
        return toJS( this.presentedDocElements[this.presentingDocument?.slides[this.presentingDocument?.currentSlide]?.id] ?? [] )
    }

    get getPresentedDocElements () {
        return toJS( this.presentedDocElements )
    }

    get getReadOnlyZoomAndPan () {
        return toJS( this.readOnlyZoomAndPan )
    }

    get getPresentingDocument () {
        return toJS( this.presentingDocument )
    }

    get getSharingUsers () {
        return toJS( this.sharingUsers )
    }

    get getRaisedHands () {
        return toJS( this.handRaiseQueue )
    }

    get getPendingRequests () {
        return toJS( this.pendingRequests )
    }

    get getVideoProcessor () {
        return toJS( this.videoProcessor )
    }

    get getLocalVideoTrack () {
        return toJS( this.videoTrack )
    }

    get getLocalAudioTrack () {
        return toJS( this.audioTrack )
    }

    get getMeetingState () {
        return toJS( this.meetingState )
    }

    get getCurrentMeeting () {
        return toJS( this.currentMeeting )
    }

    get getCurrentMeetingSettings () {
        return toJS( this.currentMeetingSettings )
    }

    get getRemoteUsers () {
        return toJS( this.remoteUsers )
    }

    get getPollResults () {
        return toJS( this.pollResults )
    }

    get getPollQueue () {
        return toJS( this.pollQueue )
    }

    get getLocalUser () {
        return toJS( this.localUser )
    }

    get getIoDevices () {
        return toJS( this.ioDevices )
    }

    get getNewJoins () {
        return toJS( this.newJoins )
    }

    get getChats () {
        return toJS( this.chats )
    }

    get getPolls () {
        return toJS( this.polls )
    }


}

const MeetingStoreInstance = new MeetingStore()

export default MeetingStoreInstance

const MeetingStoreContext = React.createContext()

export const MeetingStoreProvider = ( { children, store } ) => {
    return (
        <MeetingStoreContext.Provider value={store}>{children}</MeetingStoreContext.Provider>
    )
}

/* Hook to use store in any functional component */
export const useMeetingStore = () => React.useContext( MeetingStoreContext )